// Platform Admin -> Legal & Compliance.
//
// ConfirmPH's OWN corporate, platform, privacy, consumer, security and
// recordkeeping architecture. Deliberately not the future client-facing
// rental-business compliance product, and not visible to any rental
// business: the capability lookup returns nothing for an account that
// carries a company_id, so this page renders a plain "no access" state for
// them rather than an error.
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlertTriangle, Building2, CalendarClock, FileText, Gavel, History, Lock, ScrollText,
  ShieldAlert, ShieldCheck, Store,
} from "lucide-react";
import { toast } from "sonner";

import { CAP, canAny, legalError, plGet } from "../../lib/platformLegal";
import { Empty, Loading, Note, Section, Tabs } from "../../components/platform-legal/ui";
import AuditSection from "../../components/platform-legal/AuditSection";
import CasesSection from "../../components/platform-legal/CasesSection";
import DocumentsSection from "../../components/platform-legal/DocumentsSection";
import IdentitySection from "../../components/platform-legal/IdentitySection";
import MerchantsSection from "../../components/platform-legal/MerchantsSection";
import OverviewSection from "../../components/platform-legal/OverviewSection";
import PrivacySection from "../../components/platform-legal/PrivacySection";
import RegistersSection from "../../components/platform-legal/RegistersSection";
import SecuritySection from "../../components/platform-legal/SecuritySection";

const TABS = [
  { key: "overview", label: "Overview", icon: ShieldCheck, caps: [CAP.view] },
  { key: "identity", label: "Identity & launch", icon: Building2, caps: [CAP.view] },
  { key: "documents", label: "Legal documents", icon: ScrollText, caps: [CAP.view] },
  { key: "merchants", label: "Merchants", icon: Store, caps: [CAP.merchants] },
  { key: "complaints", label: "Complaints", icon: Gavel, caps: [CAP.complaints] },
  { key: "safety-reports", label: "Safety reports", icon: AlertTriangle, caps: [CAP.safety] },
  { key: "privacy", label: "Privacy", icon: Lock, caps: [CAP.privacyView, CAP.privacyRequests] },
  { key: "security", label: "Security & AI", icon: ShieldAlert,
    caps: [CAP.privacyBreach, CAP.legalRequests, CAP.ai, CAP.view] },
  { key: "registers", label: "Vendors & corporate", icon: CalendarClock,
    caps: [CAP.vendors, CAP.corporate, CAP.view] },
  { key: "audit", label: "Audit & access", icon: History, caps: [CAP.view] },
];

// Alert links map onto tabs so "3 complaints overdue" opens the right place.
const LINK_TO_TAB = {
  complaints: "complaints", "safety-reports": "safety-reports",
  "privacy-requests": "privacy", "security-incidents": "security",
  "legal-requests": "security", "automated-decisions": "security",
  merchants: "merchants", vendors: "registers", calendar: "registers",
  identity: "identity", documents: "documents",
};

export default function PlatformLegal() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    plGet("/status")
      .then(setStatus)
      .catch((err) => toast.error(legalError(err)))
      .finally(() => setLoading(false));
  }, []);

  // Memoised so the tab list is not rebuilt on every render by a fresh
  // empty array literal.
  const capabilities = useMemo(() => status?.capabilities || [], [status]);
  const visible = useMemo(
    () => TABS.filter((tab) => canAny(capabilities, ...tab.caps)),
    [capabilities],
  );
  const tab = params.get("tab") || visible[0]?.key || "overview";
  const setTab = (key) => setParams({ tab: key });
  const openLink = (link) => { if (LINK_TO_TAB[link]) setTab(LINK_TO_TAB[link]); };

  if (loading) return <Loading label="Checking your compliance access..." />;

  if (!status?.has_access) {
    return (
      <div data-testid="platform-legal-page" className="space-y-4">
        <Header />
        <Section>
          <Empty testId="pl-no-access">
            You don&apos;t have access to Legal &amp; Compliance. Platform admin alone is not
            enough: privacy, breach and legal-demand records need an explicitly granted,
            audited capability.
          </Empty>
        </Section>
      </div>
    );
  }

  return (
    <div data-testid="platform-legal-page" className="space-y-4">
      <Header />
      <Tabs tabs={visible} active={tab} onChange={setTab} testId="pl-tabs" />
      <div key={tab}>
        {tab === "overview" && (
          <OverviewSection capabilities={capabilities} onOpenTab={openLink} />
        )}
        {tab === "identity" && <IdentitySection capabilities={capabilities} />}
        {tab === "documents" && <DocumentsSection capabilities={capabilities} />}
        {tab === "merchants" && <MerchantsSection capabilities={capabilities} />}
        {tab === "complaints" && (
          <CasesSection capabilities={capabilities} mode="complaints" />
        )}
        {tab === "safety-reports" && (
          <CasesSection capabilities={capabilities} mode="safety" />
        )}
        {tab === "privacy" && <PrivacySection capabilities={capabilities} />}
        {tab === "security" && <SecuritySection capabilities={capabilities} />}
        {tab === "registers" && <RegistersSection capabilities={capabilities} />}
        {tab === "audit" && <AuditSection capabilities={capabilities} />}
      </div>
    </div>
  );
}

function Header() {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500
                      inline-flex items-center gap-1">
        <FileText className="w-3.5 h-3.5" /> ConfirmPH&apos;s own compliance
      </div>
      <h1 className="font-display text-2xl sm:text-3xl font-bold mt-1">Legal &amp; Compliance</h1>
      <p className="text-sm text-slate-600 mt-1 max-w-3xl">
        Corporate identity, launch readiness, the legal stack, merchant governance, complaints,
        safety reports, privacy, security and corporate records — for ConfirmPH itself. Rental
        businesses never see any of this.
      </p>
      <Note tone="slate" testId="pl-header-note">
        Absence of data is never compliance. Every fact here starts unknown and only moves when a
        person records real evidence; nothing in this centre incorporates a company, registers
        with an agency, appoints anyone, or notifies a regulator.
      </Note>
    </div>
  );
}
